﻿interface RadarChartProps {
  dimensions: { label: string; score: number }[];
  size?: number;
}

const LEVELS = [20, 40, 60, 80, 100];

export function RadarChart({ dimensions, size = 260 }: RadarChartProps) {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.36;
  const angleSlice = (2 * Math.PI) / 5;
  const startAngle = -Math.PI / 2;

  const getPoint = (i: number, value: number) => {
    const angle = startAngle + i * angleSlice;
    const r = (value / 100) * radius;
    return {
      x: cx + r * Math.cos(angle),
      y: cy + r * Math.sin(angle),
    };
  };

  const getLabelPoint = (i: number) => {
    const angle = startAngle + i * angleSlice;
    const labelR = radius + 28;
    return {
      x: cx + labelR * Math.cos(angle),
      y: cy + labelR * Math.sin(angle),
    };
  };

  const levelPolygons = LEVELS.map((level) => {
    const points = Array.from({ length: 5 }, (_, i) => {
      const p = getPoint(i, level);
      return `${p.x},${p.y}`;
    }).join(" ");
    return { level, points };
  });

  const dataPoints = dimensions.map((d, i) => {
    const p = getPoint(i, d.score);
    return `${p.x},${p.y}`;
  }).join(" ");

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      className="mx-auto"
    >
      {levelPolygons.map(({ level, points }) => (
        <polygon
          key={level}
          points={points}
          fill="none"
          stroke="#334155"
          strokeWidth="0.5"
          opacity={0.5}
        />
      ))}

      {LEVELS.slice(0, 4).map((level) => {
        const p = getPoint(0, level);
        return (
          <text
            key={level}
            x={p.x + 4}
            y={p.y - 4}
            fill="#475569"
            fontSize="8"
            textAnchor="start"
          >
            {level}
          </text>
        );
      })}

      {Array.from({ length: 5 }, (_, i) => {
        const p = getPoint(i, 100);
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={p.x}
            y2={p.y}
            stroke="#334155"
            strokeWidth="0.5"
          />
        );
      })}

      <polygon
        points={dataPoints}
        fill="rgba(59, 130, 246, 0.15)"
        stroke="#3b82f6"
        strokeWidth="2"
        strokeLinejoin="round"
      />

      {dimensions.map((d, i) => {
        const p = getPoint(i, d.score);
        return (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="4"
            fill="#3b82f6"
            stroke="#1e293b"
            strokeWidth="2"
          />
        );
      })}

      {dimensions.map((d, i) => {
        const p = getPoint(i, d.score);
        const label = getLabelPoint(i);
        const scoreX = p.x + (label.x - p.x) * 0.25;
        const scoreY = p.y + (label.y - p.y) * 0.25;
        return (
          <text
            key={i}
            x={scoreX}
            y={scoreY}
            fill="#f1f5f9"
            fontSize="10"
            fontWeight="bold"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {d.score.toFixed(0)}
          </text>
        );
      })}

      {dimensions.map((d, i) => {
        const p = getLabelPoint(i);
        return (
          <text
            key={i}
            x={p.x}
            y={p.y}
            fill="#94a3b8"
            fontSize="11"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {d.label}
          </text>
        );
      })}
    </svg>
  );
}